import { MutableRefObject } from 'react';
import { VirtualItem, useVirtual } from 'react-virtual';

type Props = {
  numRows: number;
  shouldPinFirstRow: boolean | number;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  overscan?: number;
  enabled?: boolean;
};

export const useVirtualizedRows = ({
  numRows,
  shouldPinFirstRow,
  containerRef,
  overscan,
  enabled = true,
}: Props): {
  virtualRows: VirtualItem[];
  virtualPaddingTop: string | null;
  virtualPaddingBottom: string | null;
  scrollToIndex: (index: number) => void;
} => {
  const pinnedCount = enabled ? Number(shouldPinFirstRow) : 0;

  const {
    virtualItems: virtualRows,
    totalSize,
    scrollToIndex: _scrollToIndex,
  } = useVirtual({
    size: numRows,
    parentRef: containerRef,
    overscan,
  });

  if (!enabled) {
    return {
      virtualRows: [],
      virtualPaddingTop: null,
      virtualPaddingBottom: null,
      scrollToIndex: () => {},
    };
  }

  // This calculates the top and bottom padding for the virtualized container
  // This logic is from https://tanstack.com/table/v8/docs/examples/react/virtualized-rows
  const scrollRows = virtualRows.filter((row) => row.index >= pinnedCount);
  const pinnedHeight = virtualRows.find((row) => row.index === pinnedCount)?.start ?? 0;
  const virtualPaddingTop =
    scrollRows.length > 0 ? (scrollRows?.[0]?.start || 0) - pinnedHeight : 0;
  const virtualPaddingBottom =
    scrollRows.length > 0 ? totalSize - (scrollRows?.[scrollRows.length - 1]?.end || 0) : 0;

  return {
    virtualRows: scrollRows,
    virtualPaddingTop: virtualPaddingTop > 0 ? `${virtualPaddingTop}px` : null,
    virtualPaddingBottom: virtualPaddingBottom > 0 ? `${virtualPaddingBottom}px` : null,
    scrollToIndex: (index: number) => _scrollToIndex(index, { align: 'center' }),
  };
};
